/**
 * @fileoverview Section "Toasts" — notifications transitoires (Sonner).
 *
 * 4 variantes sémantiques : success, error, warning, info. Un Toast est
 * **transitoire** (auto-dismiss ~4s par défaut) et **non-bloquant** : ne
 * jamais l'utiliser pour communiquer une erreur qui exige une action
 * immédiate (utiliser `AlertDialog` ou `Alert` à la place).
 *
 * Cas d'usage GNPC :
 *   - `success` : action réussie (copie, sauvegarde, rejeu programmé).
 *   - `error` : échec récupérable (retry proposé, action non destructive).
 *     Pour un échec sur écriture financière, préférer `AlertDialog` bloquant.
 *   - `warning` : attention réversible (dérive détectée, session bientôt
 *     expirée).
 *   - `info` : notification informationnelle (nouvelle version disponible).
 */

import { Button } from '@gnpc/ui';
import { toast } from 'sonner';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionToasts(): JSX.Element {
  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Un <code>&lt;Toaster&gt;</code> unique est monté à la racine dans <code>App.tsx</code>.
        Appeler <code>toast.success('…')</code> depuis n'importe quel composant.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DsPreview label='toast.success — action réussie' align="center">
          <Button
            variant="outline"
            onClick={() =>
              toast.success('Rejeu programmé', {
                description: 'Un nouvel appel EnregistrerPaiement est en file d\'attente.',
              })
            }
          >
            Succès
          </Button>
        </DsPreview>

        <DsPreview label='toast.error — échec récupérable' align="center">
          <Button
            variant="outline"
            onClick={() =>
              toast.error('Impossible de charger la liste', {
                description: 'Vérifier la disponibilité du service.',
                action: { label: 'Réessayer', onClick: () => toast.info('Retry lancé…') },
              })
            }
          >
            Erreur
          </Button>
        </DsPreview>

        <DsPreview label='toast.warning — attention réversible' align="center">
          <Button
            variant="outline"
            onClick={() =>
              toast.warning('Session expire dans 5 min', {
                description: 'Toute action pendante sera perdue.',
              })
            }
          >
            Attention
          </Button>
        </DsPreview>

        <DsPreview label='toast.info — notification' align="center">
          <Button
            variant="outline"
            onClick={() =>
              toast.info('Nouvelle version disponible', {
                description: 'Rechargez la page pour bénéficier des dernières mises à jour.',
              })
            }
          >
            Info
          </Button>
        </DsPreview>
      </div>

      <div className="rounded-lg border border-quipux-orange-200 bg-quipux-orange-50 p-4 text-xs text-quipux-navy-900">
        <p className="mb-1 font-semibold">Anti-patterns</p>
        <ul className="list-inside list-disc space-y-1">
          <li>
            <b>Pas de toast pour une action critique</b> — si l'utilisateur DOIT lire le
            message, utiliser <code>AlertDialog</code>.
          </li>
          <li>
            <b>Pas de toast auto-lancé au chargement</b> — polluant. Réserver aux réactions
            à une action utilisateur.
          </li>
          <li>
            <b>Pas de toast persistant</b> — un état durable est un <code>Banner</code>{' '}
            (bandeau haut de page), pas un toast.
          </li>
        </ul>
      </div>
    </div>
  );
}
